<script setup lang="ts">
import { useTranslation } from '@/plugins/i18n/useTranslation';
import type { Alert } from '@/stores/alertStore';
import BaseButton from './BaseButton.vue';

type AlertProps = {
  closable?: boolean;
  alert: Alert;
};

const props = withDefaults(defineProps<AlertProps>(), {
  title: '',
  text: '',
  closable: true,
  color: 'info'
});

const { t } = useTranslation();

const emit = defineEmits<{
  (e: 'close'): void;
}>();
</script>
<template>
  <div>
    <v-alert
      :title="t(props.alert.title || '')"
      :text="t(props.alert.message)"
      :color="props.alert.color"
      :icon="props.alert.icon"
      :closable="false"
      :class="[props.alert.alertClass]"
      @click:close="emit('close')"
    >
      <template #title>
        <div class="d-flex justify-space-between w-100 align-center">
          <span
            v-if="props.alert.title"
            class="d-flex align-center w-100 h-100"
          >{{
            t(props.alert.title)
          }}</span>
          <BaseButton
            v-if="props.closable"
            icon="mdi-close"
            size="x-small"
            color="default"
            variant="text"
            :icon-size="14"
            @click="emit('close')"
          />
        </div>
      </template>
      <template #text>
        <template v-if="props.alert.route">
          <RouterLink
            :to="props.alert.route"
            class="d-inline-block pa-3 text-decoration-underline text-primary"
          >
            {{ t(props.alert.message) }}
          </RouterLink>
        </template>
        <template v-else>
          <span class="d-inline-block pa-3 alert-text">{{ t(props.alert.message) }}</span>
        </template>
      </template>
    </v-alert>
  </div>
</template>
<style scoped lang="scss">
:deep(.v-alert) {
  padding: 0;
  font-size: 14px;

  .v-alert-title {
    min-height: 32px;
    padding: 4px 12px;

    span {
      line-height: 21px;
      font-weight: 700;
      font-size: 14px;
    }
  }

  .v-alert__close {
    margin-left: 0;
    height: 100%;
  }

  &.success {
    .v-alert-title {
      background-color: rgb(var(--v-theme-success-450));
      color: rgb(var(--v-theme-success-600));
    }

    .alert-text {
      color: rgb(var(--v-theme-success-600));
    }

    .v-alert__close {
      background-color: rgb(var(--v-theme-success-450));
    }
  }

  &.info {
    .v-alert-title {
      background-color: var(--toast-info-header-background-color);
      color: var(--toast-info-text-color);
    }

    .alert-text {
      color: var(--toast-info-text-color);
    }

    .v-alert__close {
      background-color: var(--toast-info-header-background-color);
    }
  }

  &.error {
    .v-alert-title {
      background-color: rgb(var(--v-theme-error-200));
      color: rgb(var(--v-theme-error-600));
    }

    .alert-text {
      color: rgb(var(--v-theme-error-600));
    }

    .v-alert__close {
      background-color: rgb(var(--v-theme-error-200));
    }
  }
}
</style>
